export const isServer = () => typeof window === "undefined";

export const isBrowser = () => !isServer();

/**
 * Trailing-edge debounce: `fn` runs once, with the latest arguments, `wait` ms
 * after the last call. The one shape this codebase uses (`debounce(fn, 250)`),
 * kept local because lodash-es `debounce` pulls `toNumber` and friends into the
 * shared _app chunk through the header search box (WHO-5479, S5).
 */
export const debounce = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  wait: number
) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: Args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

/**
 * ASCII-only `kebabCase`. Matches lodash-es `kebabCase` for every one of the
 * 4685 translation keys we ship, plus camelCase/PascalCase/SCREAMING_SNAKE and
 * acronyms (`XMLHttpRequest` -> `xml-http-request`).
 *
 * ponytail: lodash-es `kebabCase` drags in `_createCompounder` -> `deburr` and
 * its Unicode transliteration tables — ~30 KB minified, and it landed in the
 * shared _app chunk because `useLogMissingKeys` (called from `_app`) used it.
 * The one behaviour dropped is accent folding (`héllo` -> `h-llo`, not
 * `hello`); every caller feeds ASCII keys. Use lodash if you need real deburr.
 */
export const kebabCase = (value: string) =>
  (
    value.match(/[A-Z]{2,}(?=[A-Z][a-z]+|\d|\W|$)|[A-Z]?[a-z]+|[A-Z]|\d+/g) ??
    []
  )
    .join("-")
    .toLowerCase();
